import React, { useRef, useState } from 'react';
import useForm from 'hooks/useForm';
import { AffiliationRole } from 'utils/constants';
import { validateEmail } from 'utils/helpers';
import Button from 'components/Button/Button';
import Modal, { ModalRef } from 'components/Modal/Modal';
import AGForm from 'components/AGForm/AGForm';
import TextInput from 'components/AGForm/Inputs/TextInput/TextInput';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import Label from 'components/AGForm/Inputs/Label/Label';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import { useTeam } from 'contexts/TeamContext';

const options = [
    {
        value: '',
        label: 'Select a role',
    },
    {
        value: AffiliationRole.ADMIN,
        label: AffiliationRole.ADMIN + ' (Full access to view, edit, and delete data)',
    },
    {
        value: AffiliationRole.EMPLOYEE,
        label: AffiliationRole.EMPLOYEE + ' (Can view/edit data, but no access to financial data)',
    },
    {
        value: AffiliationRole.ENFORCER,
        label: AffiliationRole.ENFORCER + ' (Can only access enforcements)',
    },
];

type FormState = {
    firstName: string;
    lastName: string;
    email: string;
    role: AffiliationRole | '';
};

const initialState: FormState = {
    firstName: '',
    lastName: '',
    email: '',
    role: '',
};

type InvitationFormProps = {
    orgId: number;
};

const errorInitialState = { firstName: '', lastName: '', email: '', role: '' };

function InvitationForm({ orgId }: InvitationFormProps) {
    const { loading, inviteTeamMember } = useTeam();
    const { handleInput, handleValueChange, inputs } = useForm(initialState);
    const [errors, setErrors] = useState(errorInitialState);
    const addTeamMemberRef = useRef<ModalRef>();

    const isFormComplete = inputs.firstName && inputs.lastName && inputs.email && inputs.role;

    const handleSubmit = () => {
        const newErrors = { ...errorInitialState };
        const validatedEmail = validateEmail(inputs.email);

        if (!isFormComplete || !validatedEmail) {
            if (inputs.firstName === '') newErrors.firstName = 'Please enter a first name';
            if (inputs.lastName === '') newErrors.lastName = 'Please enter a last name';
            if (!validatedEmail || inputs.email === '') newErrors.email = 'Please enter a valid email';
            if (!inputs.role) newErrors.role = 'Please choose a role';
        } else {
            inviteTeamMember(
                {
                    first_name: inputs.firstName,
                    last_name: inputs.lastName,
                    email: inputs.email,
                    role: inputs.role as AffiliationRole,
                },
                orgId
            );
            addTeamMemberRef.current?.closeModal();
        }
        setErrors(newErrors);
    };

    function handleEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
        if (errors.email && validateEmail(event.target.value)) {
            setErrors({ ...errors, email: '' });
        }
        handleInput(event);
    }

    return (
        <>
            <div>
                <Button size="sm" onClick={() => addTeamMemberRef.current?.openModal()} style={{ width: 180 }}>
                    + Add Team Member
                </Button>
            </div>
            <Modal ref={addTeamMemberRef} title="Add a Team Member">
                <AGForm>
                    <div>
                        <Label label="Role *" />
                        <AGSimpleSelect
                            selected={inputs.role}
                            onSelect={(selection) => handleValueChange('role', selection as AffiliationRole)}
                            options={options}
                            wrapOptions
                            width="340px"
                        />
                        {errors.role && <ErrorMessage errorMessage={errors.role} />}
                    </div>
                    <TextInput
                        label="First Name *"
                        onChange={handleInput}
                        placeholder="Required"
                        errors={errors.firstName}
                        name="firstName"
                        value={inputs.firstName}
                    />
                    <TextInput
                        label="Last Name *"
                        onChange={handleInput}
                        placeholder="Required"
                        errors={errors.lastName}
                        name="lastName"
                        value={inputs.lastName}
                    />
                    <TextInput
                        label="Email *"
                        onChange={handleEmailChange}
                        placeholder="Required"
                        errors={errors.email}
                        name="email"
                        value={inputs.email}
                    />

                    <Button type="button" onClick={handleSubmit} disabled={!isFormComplete || loading}>
                        Add Team Member
                    </Button>
                </AGForm>
            </Modal>
        </>
    );
}

export default InvitationForm;
